import {Action, Module, Mutation} from 'vuex-module-decorators'
import store from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import QueryShortDto = MdbsCore.QueryShortDto;
import QueryDto = MdbsCore.QueryDto;
import IQueryAnswerDto = MdbsCore.IQueryAnswerDto;
import QueryAnswerDto = MdbsCore.QueryAnswerDto;
import IQueryCreateDto = MdbsCore.IQueryCreateDto;
import QueryCreateDto = MdbsCore.QueryCreateDto;
import IQueryAcceptDto = MdbsCore.IQueryAcceptDto;
import IQuerySolveDto = MdbsCore.IQuerySolveDto;
import IQueryRevokeDto = MdbsCore.IQueryRevokeDto;
import QueryRevokeDto = MdbsCore.QueryRevokeDto;
import QueryAcceptDto = MdbsCore.QueryAcceptDto;
import QuerySolveDto = MdbsCore.QuerySolveDto;
import IQueryOverviewShortDto = MdbsCore.IQueryOverviewShortDto;
import IQueryEditDto = MdbsCore.IQueryEditDto;
import QueryEditDto = MdbsCore.QueryEditDto;
import IQueryDeferDto = MdbsCore.IQueryDeferDto;
import QueryDeferDto = MdbsCore.QueryDeferDto;
import IQueryListResponse = MdbsCore.IQueryListResponse;
import QueryListResponse = MdbsCore.QueryListResponse;
import VisitContextCache from "@/models/visit.context.cache";

const queryClient = new MdbsCore.QueryClient();

@Module({namespaced: true, name: 'query', store: store})
export default class QueryModule extends BaseModule {
    _queriesForPatientFields: Array<QueryShortDto> = [];
    _queriesForVisitFields: Array<QueryShortDto> = [];
    _queriesForInstrumentFields: Array<QueryShortDto> = [];
    _patientQueryListResponse: MdbsCore.IQueryListResponse = new QueryListResponse();
    _queriesForPatientMy: Array<QueryShortDto> = [];
    _visitQueryListResponse: MdbsCore.IQueryListResponse = new QueryListResponse();
    _instrumentQueryListResponse: MdbsCore.IQueryListResponse = new QueryListResponse();
    _instituteQueryListResponse: MdbsCore.IQueryListResponse = new QueryListResponse();
    _patientVisitQueries: any = {};

    get patientQueryListResponse() {
        return this._patientQueryListResponse;
    }

    get patientVisitQueries() {
        return this._patientVisitQueries;
    }

    get patientVisitQueriesById() {
        return (visitId: string) => {
            return this._patientVisitQueries[visitId] ? this._patientVisitQueries[visitId]  : undefined;
        }
    }

    get visitQueryListResponse() {
        return this._visitQueryListResponse;
    }
    get instrumentQueryListResponse() {
        return this._instrumentQueryListResponse;
    }
    get instituteQueryListResponse() {
        return this._instituteQueryListResponse;
    }

    get queriesForPatientMy() {
        return this._queriesForPatientMy;
    }

    get queriesForPatientFields() {
        return this._queriesForPatientFields;
    }

    get queriesForPatientField() {
        return (fieldId: string) => {
            const queries = this.queriesForPatientFields.filter(q => q.referenceId == fieldId);

            return queries;
        }
    }

    get queriesForFields() {
        return (referenceType: string, fieldId: string) => {
            if (referenceType == "PatientDefinition")
                return this.queriesForPatientField(fieldId);
            if (referenceType == "VisitDefinition")
                return this.queriesForVisitField(fieldId);
            if (referenceType == "InstrumentDefinition")
                return this.queriesForInstrumentField(fieldId);
        }
    }

    get queriesForVisitFields() {
        return this._queriesForVisitFields;
    }

    get queriesForVisitField() {
        return (fieldId: string) => {
            const queries = this.queriesForVisitFields.filter(q => q.referenceId == fieldId);

            return queries;
        }
    }

    get queriesForInstrumentFields() {
        return this._queriesForInstrumentFields;
    }

    get queriesForInstrumentField() {
        return (fieldId: string) => {
            const queries = this.queriesForInstrumentFields.filter(q => q.referenceId == fieldId);

            return queries;
    }
    }

    @Mutation
    setPatientQueryListResponse(patientQueryListResponse: IQueryListResponse) {
        this._patientQueryListResponse = patientQueryListResponse;
    }

    @Mutation
    setPatientVisitQuery(payload:{visitId: string, queryDto: QueryListResponse}) {
        //Vue.set(this._patientVisitQueries, payload.visitId, payload.queryDto)
        this._patientVisitQueries[payload.visitId] = payload.queryDto;
        this._patientVisitQueries = Object.assign({}, this._patientVisitQueries);
    }

    @Mutation
    setVisitQueryListResponse(visitQueryListResponse: IQueryListResponse) {
        this._visitQueryListResponse = visitQueryListResponse;
    }

    @Mutation
    setInstrumentQueryListResponse(instrumentQueryListResponse: IQueryListResponse) {
        this._instrumentQueryListResponse = instrumentQueryListResponse;
    }

    @Mutation
    setInstituteQueryListResponse(instituteQueryListResponse: IQueryListResponse) {
        this._instituteQueryListResponse = instituteQueryListResponse;
    }

    @Mutation
    setQueriesForPatientMy(queries: Array<QueryShortDto>) {
        this._queriesForPatientMy = queries;
    }

    @Mutation
    setQueriesForPatientFields(queries: Array<QueryShortDto>) {
        this._queriesForPatientFields = queries;
    }

    @Mutation
    setQueriesForVisitFields(queries: Array<QueryShortDto>) {
        this._queriesForVisitFields = queries;
    }

    @Mutation
    setQueriesForInstrumentFields(queries: Array<QueryShortDto>) {
        this._queriesForInstrumentFields = queries;
    }

    @Mutation
    resetPatientVisitQueries() {
        this._patientVisitQueries = {};
    }

    @Mutation
    reset() {
        this._queriesForInstrumentFields = [];
        this._queriesForPatientFields = [];
        this._queriesForVisitFields = [];
        this._queriesForInstrumentFields = [];
        this._patientQueryListResponse= new QueryListResponse();
        this._queriesForPatientMy = [];
        this._visitQueryListResponse= new QueryListResponse();
        this._instrumentQueryListResponse= new QueryListResponse();
        this._instituteQueryListResponse= new QueryListResponse();
        this._patientVisitQueries = {}
    }

    @Action({rawError: true})
    async loadQuery(queryId: string): Promise<QueryDto> {
        let result: QueryDto = new QueryDto();

        await this.execute(async () => {
            result = await queryClient.getById(queryId);
        });

        if (!result?.id) throw new Error(`Could not find query ${queryId}`);

        return result!;
    }

    @Action({rawError: true})
    async loadQueryOverview(payload: { page: number, itemsPerPage: number, sortBy: string[], sortDesc: boolean[], studyPatientId: string | undefined, studyInstituteId: string | undefined, searchText: string | undefined }): Promise<{ itemsTotal: number, items: Array<IQueryOverviewShortDto> }> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {page, itemsPerPage, sortBy, sortDesc, studyPatientId, studyInstituteId, searchText} = payload;
        let result: { itemsTotal: number, items: Array<IQueryOverviewShortDto> } | null = null;

        await this.execute(async () => {
            const response = await queryClient.getOverview(page, itemsPerPage, sortBy, sortDesc, studyPatientId, studyInstituteId, searchText);
            result = {
                items: response.items ?? [],
                itemsTotal: response.itemsTotal ?? 0
            };
        });

        return result!;
    }

    @Action({rawError: true})
    async loadMyQueries(payload: { latestCount: number | undefined }): Promise<IQueryListResponse> {
        const {latestCount} = payload;
        let result: IQueryListResponse | null = null;

        await this.execute(async () => {
            result = await queryClient.getMy(latestCount);
        });

        return result!;
    }

    @Action({rawError: true})
    async loadPatientMyQueries(studyPatientId: string | undefined) {
        await this.execute(async () => {
            const result = await queryClient.getMyForPatient(studyPatientId);
            this.setQueriesForPatientMy(result.items ?? []);
        });
    }

    @Action({rawError: true})
    async loadQueries(payload: { referenceType: string, referenceId: string }) {
        await this.execute(async () => {
            let response: IQueryListResponse = new QueryListResponse();
            if (payload.referenceType == "Patient") {
                response = await queryClient.getForPatient(payload.referenceId);
                this.setPatientQueryListResponse(response);
            }
            if (payload.referenceType == "Visit") {
                console.log("UPDATE VISIT QUERY", payload.referenceId)
                await this.loadPatientVisitQueries(payload.referenceId);
            }
            if (payload.referenceType == "Instrument") {
                response = await queryClient.getForInstrument(payload.referenceId);
                this.setInstrumentQueryListResponse(response);
            }
            if (payload.referenceType == "StudyInstitute") {
                response = await queryClient.getForInstitute(payload.referenceId);
                this.setInstituteQueryListResponse(response);
            }
        });
    }

    @Action({rawError: true})
    async loadPatientQueries(studyPatientId: string | undefined) {
        await this.execute(async () => {
            const result = await queryClient.getForPatient(studyPatientId);
            this.setPatientQueryListResponse(result);
        });
    }

    @Action({rawError: true})
    async loadVisitQueries(visitId: string | undefined) {
        await this.execute(async () => {
            const result = await queryClient.getForVisit(visitId);
            this.setVisitQueryListResponse(result);
        });
    }

    @Action({rawError: true})
    async loadPatientVisitsQueries(visits: VisitContextCache[]) {
        await this.execute(async () => {
            this.resetPatientVisitQueries();
            for (const visit of visits) {
                if (visit.isNew)
                    continue;
                const result = await queryClient.getForVisit(visit.id);
                this.setPatientVisitQuery({visitId: visit.id, queryDto: result});
            }
        });
    }

    @Action({rawError: true})
    async loadPatientVisitQueries(visitId: string) {
            await this.execute(async () => {
                const result = await queryClient.getForVisit(visitId);
                this.setPatientVisitQuery({visitId: visitId, queryDto: result});
            });
        }

    @Action({rawError: true})
    async loadInstrumentQueries(instrumentId: string | undefined) {
        await this.execute(async () => {
            const result = await queryClient.getForInstrument(instrumentId);
            this.setInstrumentQueryListResponse(result);
        });
    }

    @Action({rawError: true})
    async loadInstituteQueries(instituteId: string | undefined) {
        await this.execute(async () => {
            const result = await queryClient.getForInstitute(instituteId);
            this.setInstituteQueryListResponse(result);
        });
    }

    @Action({rawError: true})
    async loadQueriesForFields(payload: { referenceType: string, referenceId: string }) {
        await this.execute(async () => {
            let queries: IQueryListResponse = new QueryListResponse();
            if (payload.referenceType == "PatientDefinition") {
                queries = await queryClient.getAllForPatient(payload.referenceId);
                this.setQueriesForPatientFields(queries.items ?? []);
            }
            if (payload.referenceType == "VisitDefinition") {
                queries = await queryClient.getAllForVisit(payload.referenceId);
                this.setQueriesForVisitFields(queries.items ?? []);
            }
            if (payload.referenceType == "InstrumentDefinition") {
                queries = await queryClient.getAllForInstrument(payload.referenceId);
                this.setQueriesForInstrumentFields(queries.items ?? []);
            }
        });
    }


    @Action({rawError: true})
    async loadQueriesForPatientFields(instrumentId: string) {
        await this.execute(async () => {
            const queries = await queryClient.getAllForPatient(instrumentId);
            this.setQueriesForPatientFields(queries.items ?? []);
        });
    }

    @Action({rawError: true})
    async loadQueriesForVisitFields(instrumentId: string) {
        await this.execute(async () => {
            const queries = await queryClient.getAllForVisit(instrumentId);
            this.setQueriesForVisitFields(queries.items ?? []);
        });
    }

    @Action({rawError: true})
    async loadQueriesForInstrumentFields(instrumentId: string) {
        await this.execute(async () => {
            const queries = await queryClient.getAllForInstrument(instrumentId);
            this.setQueriesForInstrumentFields(queries.items ?? []);
        });
    }

    @Action({rawError: true})
    async createQueryForPatient(payload: { patientId: string, createDto: IQueryCreateDto }) {
        await this.execute(async () => {
            const dto = QueryCreateDto.fromJS(payload.createDto);
            await queryClient.createForPatient(dto);
            await this.loadPatientQueries(payload.patientId)
        });
    }

    @Action({rawError: true})
    async createQueryForVisit(payload: { visitId: string, createDto: IQueryCreateDto }) {
        await this.execute(async () => {
            const dto = QueryCreateDto.fromJS(payload.createDto);
            await queryClient.createForVisit(dto);
            await this.loadPatientVisitQueries(payload.visitId)
        });
    }

    @Action({rawError: true})
    async createQueryForInstrument(payload: { instrumentId: string, createDto: IQueryCreateDto }) {
        await this.execute(async () => {
            const dto = QueryCreateDto.fromJS(payload.createDto);
            await queryClient.createForInstrument(dto);
            await this.loadInstrumentQueries(payload.instrumentId)
        });
    }

    @Action({rawError: true})
    async createQueryForInstitute(payload: { instituteId: string, createDto: IQueryCreateDto }) {
        await this.execute(async () => {
            const dto = QueryCreateDto.fromJS(payload.createDto);
            await queryClient.createForInstitute(dto);
            await this.loadInstituteQueries(payload.instituteId)
        });
    }

    @Action({rawError: true})
    async createQueryForField(payload: { referenceType: string, referenceId: string, createDto: IQueryCreateDto }) {
        await this.execute(async () => {
            const dto = QueryCreateDto.fromJS(payload.createDto);
            await queryClient.createForField(dto);
            await this.loadQueriesForFields({referenceType: payload.referenceType, referenceId: payload.referenceId})
        });
    }

    @Action({rawError: true})
    async editQuery(editDto: IQueryEditDto) {
        await this.execute(async () => {
            const dto = QueryEditDto.fromJS(editDto);
            await queryClient.edit(editDto.id, dto);
        });
    }

    @Action({rawError: true})
    async answerQuery(answerDto: IQueryAnswerDto) {
        await this.execute(async () => {
            const dto = QueryAnswerDto.fromJS(answerDto);
            await queryClient.answer(answerDto.id, dto);
        });
    }

    @Action({rawError: true})
    async revokeQuery(revokeDto: IQueryRevokeDto) {
        await this.execute(async () => {
            const dto = QueryRevokeDto.fromJS(revokeDto);
            await queryClient.revoke(revokeDto.id, dto);
        });
    }

    @Action({rawError: true})
    async acceptQuery(acceptDto: IQueryAcceptDto) {
        await this.execute(async () => {
            const dto = QueryAcceptDto.fromJS(acceptDto);
            await queryClient.accept(acceptDto.id, dto);
        });
    }

    @Action({rawError: true})
    async solveQuery(solveDto: IQuerySolveDto) {
        await this.execute(async () => {
            const dto = QuerySolveDto.fromJS(solveDto);
            await queryClient.solve(solveDto.id, dto);
        });
    }

    @Action({rawError: true})
    async deferQuery(deferDto: IQueryDeferDto) {
        await this.execute(async () => {
            const dto = QueryDeferDto.fromJS(deferDto);
            await queryClient.defer(deferDto.id, dto);
        });
    }
}
